import { pb } from '@/core/api';
import { getFingerprint } from '@/utils/fingerprint';

export type IdentityState =
  | { status: 'checking' }
  | { status: 'ok' }
  | { status: 'blocked'; expiresAt?: string; reason?: string };

type CheckResponse = {
  blocked?: boolean;
  reason?: string | null;
  expires_at?: string | null;
};

const TTL_MS = 5_000;
let cached: { value: IdentityState; ts: number } | null = null;
let inflight: Promise<IdentityState> | null = null;

function toState(res: CheckResponse): IdentityState {
  if (!res?.blocked) return { status: 'ok' };
  const out: IdentityState = { status: 'blocked' };
  if (res.expires_at) out.expiresAt = res.expires_at;
  if (res.reason) out.reason = res.reason;
  return out;
}

export function getCachedIdentity(): IdentityState | null {
  if (!cached) return null;
  if (Date.now() - cached.ts >= TTL_MS) return null;
  return cached.value;
}

export function invalidateIdentityCache(): void {
  cached = null;
  inflight = null;
}

export async function checkIdentity(): Promise<IdentityState> {
  const fresh = getCachedIdentity();
  if (fresh) return fresh;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const fp = await getFingerprint();
      const res: CheckResponse = await pb.send(
        '/api/registration/check-identity',
        { method: 'POST', body: { fingerprint: fp } }
      );
      const state = toState(res);
      cached = { value: state, ts: Date.now() };
      return state;
    } catch {
      return { status: 'ok' };
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
